<template>
  <div class="visualize-viewer" :style="containerStyle">
    <OfflineMessage v-if="isOffline" />

    <div
      v-if="data.link && data.link.length !== 0 && isReady && showPrimaryIframe"
      class="iframe-wrapper primary-iframe"
      :class="{ active: activePrimaryIframe }"
      :style="iframeWrapperStyle"
    >
      <iframe
        ref="primaryIframe"
        class="iframe-container"
        :src="primaryIframeSource"
        scrolling="no"
        :style="iframeStyle"
        @load="onPrimaryIframeLoad"
      />
    </div>

    <div
      v-if="data.link && data.link.length !== 0 && isReady && showSecondaryIframe"
      class="iframe-wrapper secondary-iframe"
      :class="{ active: !activePrimaryIframe }"
      :style="iframeWrapperStyle"
    >
      <iframe
        ref="secondaryIframe"
        class="iframe-container"
        :src="secondaryIframeSource"
        scrolling="no"
        :style="iframeStyle"
        @load="onSecondaryIframeLoad"
      />
    </div>

    <div v-if="!data.link || data.link.length === 0" class="visualize-placeholder">
      <i class="material-icons-outlined align-middle">bar_chart</i>
      Visualize Data
    </div>
  </div>
</template>

<script>
  import { duration } from 'moment';
  import { getRBGAColor } from '@/helpers/utils';
  import { getVisualizationToken } from '@/api/sync';
  import { logger } from '@/utils/screenEventsLogger';
  import config from '@/config';
  import { getConnectionStatus } from '@/utils/internetHelper';
  import OfflineMessage from '@/components/common/OfflineMessage.vue';

  const OFFLINE_RETRY_INTERVAL = 30000;

  export default {
    name: 'VisualizeViewer',

    components: {
      OfflineMessage,
    },

    props: {
      data: {
        type: Object,
        default: () => ({}),
      },
    },

    data() {
      return {
        refreshTimer: null,
        refreshCount: 0,
        activePrimaryIframe: true,
        showPrimaryIframe: true,
        showSecondaryIframe: false,
        loadingTimeout: null,
        transitionInProgress: false,
        visualizationToken: null,
        isReady: false,
        isOffline: false,
        retryTimer: null,
      };
    },

    computed: {
      containerStyle() {
        return {
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          backgroundColor: getRBGAColor(this.data.backgroundColor, this.data.opacity / 100),
        };
      },

      iframeZoom() {
        return (this.data.zoom || 100) / 100;
      },

      iframeWrapperStyle() {
        return {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          transition: 'opacity 0.3s ease-in-out',
          zIndex: 1,
        };
      },

      iframeStyle() {
        var zoom = this.iframeZoom;

        return {
          position: 'absolute',
          top: 0,
          left: 0,
          width: zoom > 0 ? 100 / zoom + '%' : '100%',
          height: zoom > 0 ? 100 / zoom + '%' : '100%',
          border: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
          transform: 'scale(' + zoom + ')',
          transformOrigin: '0 0',
        };
      },

      shouldIncludeToken() {
        return !!(this.data.link && this.data.link.includes(config.syncApiUrl));
      },

      primaryIframeSource() {
        const baseUrl = this.data.link || '';

        if (!baseUrl) return '';

        const hasQueryParams = baseUrl.includes('?');
        const separator = hasQueryParams ? '&' : '?';
        const tokenParam =
          this.shouldIncludeToken && this.visualizationToken
            ? `&token=${encodeURIComponent(this.visualizationToken)}`
            : '';

        return `${baseUrl}${separator}_refresh=${this.refreshCount}&_iframe=primary${tokenParam}`;
      },

      secondaryIframeSource() {
        const baseUrl = this.data.link || '';

        if (!baseUrl) return '';

        const hasQueryParams = baseUrl.includes('?');
        const separator = hasQueryParams ? '&' : '?';
        const tokenParam =
          this.shouldIncludeToken && this.visualizationToken
            ? `&token=${encodeURIComponent(this.visualizationToken)}`
            : '';
        const refreshCount = this.refreshCount + 1;

        return `${baseUrl}${separator}_refresh=${refreshCount}&_iframe=secondary${tokenParam}`;
      },
    },

    async mounted() {
      await this.loadContent();
      this.setupRefreshTimer();
    },

    beforeDestroy() {
      this.clearRefreshTimer();
      this.clearLoadingTimeout();
      this.clearRetryTimer();
    },

    watch: {
      data: {
        handler() {
          if (!this.isReady) {
            this.loadContent();
          }
          this.setupRefreshTimer();
        },
        deep: true,
      },
    },

    methods: {
      async loadContent() {
        this.clearRetryTimer();

        if (!this.data.link) return;

        const connected = await getConnectionStatus();

        // Show offline message instead of loading the iframe into an error page.
        if (!connected) {
          this.isOffline = true;
          this.retryTimer = setTimeout(this.loadContent, OFFLINE_RETRY_INTERVAL);
          return;
        }

        if (this.shouldIncludeToken) {
          await this.fetchVisualizationToken();
        }

        this.isOffline = false;
        this.isReady = true;
      },

      clearRetryTimer() {
        if (this.retryTimer) {
          clearTimeout(this.retryTimer);
          this.retryTimer = null;
        }
      },

      async fetchVisualizationToken() {
        try {
          this.visualizationToken = await getVisualizationToken();
        } catch (error) {
          logger.addLog(`Failed to fetch visualization token: ${error?.message}.`);
        }
      },

      setupRefreshTimer() {
        this.clearRefreshTimer();

        if (
          this.data.refresh &&
          this.data.refreshTime &&
          this.data.refreshTime > 0 &&
          this.data.link
        ) {
          const refreshInterval = duration(
            this.data.refreshTime || 24,
            this.data.timeUnit || 'hours',
          ).asMilliseconds();

          this.refreshTimer = setInterval(this.refreshLink, refreshInterval);
        }
      },

      clearRefreshTimer() {
        if (this.refreshTimer) {
          clearInterval(this.refreshTimer);
          this.refreshTimer = null;
        }
      },

      clearLoadingTimeout() {
        if (this.loadingTimeout) {
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = null;
        }
      },

      async refreshLink() {
        if (!this.isReady || this.transitionInProgress) {
          return;
        }

        this.transitionInProgress = true;

        const connected = await getConnectionStatus();

        // Keep the already loaded content instead of replacing it with an error page.
        if (!connected) {
          this.transitionInProgress = false;
          return;
        }

        this.refreshCount++;

        if (this.activePrimaryIframe) {
          this.showSecondaryIframe = true;
        } else {
          this.showPrimaryIframe = true;
        }

        this.loadingTimeout = setTimeout(() => {
          this.switchActiveIframe();
        }, 5000);
      },

      switchActiveIframe() {
        this.activePrimaryIframe = !this.activePrimaryIframe;

        setTimeout(() => {
          if (this.activePrimaryIframe) {
            this.showSecondaryIframe = false;
          } else {
            this.showPrimaryIframe = false;
          }

          this.transitionInProgress = false;
        }, 500);
      },

      onPrimaryIframeLoad() {
        if (!this.activePrimaryIframe && this.transitionInProgress) {
          this.clearLoadingTimeout();
          this.switchActiveIframe();
        }
      },

      onSecondaryIframeLoad() {
        if (this.activePrimaryIframe && this.transitionInProgress) {
          this.clearLoadingTimeout();
          this.switchActiveIframe();
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .visualize-viewer {
    position: relative;
    overflow: hidden;
  }

  .iframe-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    z-index: 1;

    &.active {
      opacity: 1;
      z-index: 2;
    }
  }

  .iframe-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
    pointer-events: none;
    overflow: hidden;
  }

  .visualize-placeholder {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    text-align: center;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    color: #757575;
    font-size: 32px;

    i {
      font-size: 64px;
    }
  }
</style>
